import React from 'react';
import styles from './LayoutWithAuth.module.css';
import {AuthContextProps, useAuth} from "react-oidc-context";
import Login from "../Login/Login";
import Header from "../Header/Header";
import {MessageBar, MessageBarType, Spinner, SpinnerSize, Stack, StackItem} from "@fluentui/react";
import Navigation from "../Navigation/Navigation";
import Footer from "../Footer/Footer";
import RBAC from "../RBAC/RBAC";
import {FnPUser} from "../../model/FnPUser";
import {LS_USER_INFO} from "../../constants/EiopaFnPConstants";
import ReactDOM from "react-dom";
import {getUserInfo} from "../../services/UserInfoService";

interface LayoutWithAuthProps {
    comp: JSX.Element
    allowedRoles: string[]
    selectedNav?: string
}

const CHECK_TOKEN_EXPIRED_INTERVAL_IN_SECONDS: number = 20;
const NOTIFY_USER_BEFORE_TOKEN_EXPIRATION_INTERVAL_IN_SECONDS: number = 20;
const NOTIFY_USER_BEFORE_TOKEN_EXPIRATION_IN_MINUTES: number = 2;

function LayoutWithAuth(props: LayoutWithAuthProps) {
    const auth: AuthContextProps = useAuth();

    React.useEffect(() => {
        let checkTokenExpiredInterval: NodeJS.Timeout;
        let notifyUserTokenExpiringInterval: NodeJS.Timeout;

        if(auth.user && auth.isAuthenticated && !auth.isLoading) {
            const tokenExpTime: number = auth.user.profile.exp;
            const notifyUserTime: number = tokenExpTime - (NOTIFY_USER_BEFORE_TOKEN_EXPIRATION_IN_MINUTES * 60);

            checkTokenExpiredInterval = setInterval(function () {
                const epochTimeNow: number = Math.floor(Date.now() / 1000);
                if(tokenExpTime <= epochTimeNow) {
                    auth.signinRedirect();
                }
            }, CHECK_TOKEN_EXPIRED_INTERVAL_IN_SECONDS * 1000);

            notifyUserTokenExpiringInterval = setInterval(function () {
                const epochTimeNow: number = Math.floor(Date.now() / 1000);
                if(notifyUserTime <= epochTimeNow) {
                    clearInterval(notifyUserTokenExpiringInterval);
                    const confirm = window.confirm(`Your session is about to expire (~${NOTIFY_USER_BEFORE_TOKEN_EXPIRATION_IN_MINUTES} minutes). Do you want to renew your session?`);
                    if (confirm) {
                        auth.signinRedirect();
                    }
                }
            }, NOTIFY_USER_BEFORE_TOKEN_EXPIRATION_INTERVAL_IN_SECONDS * 1000);
        }

        return () => {
            clearInterval(checkTokenExpiredInterval);
            clearInterval(notifyUserTokenExpiringInterval);
        }
    }, [auth.user, auth.isAuthenticated, auth.isLoading]);

    let component: JSX.Element = <></>;

    if(!auth.isLoading) {
        if (auth.user && auth.isAuthenticated) {
            let userInfo = getUserInfo(auth.user.id_token);

            component =
                <>
                    <Stack className={styles.mainLayout}>
                        <StackItem>
                            <Header user={userInfo}/>
                        </StackItem>
                        <StackItem className={styles.mainContent}>
                            <Stack>
                                <StackItem>
                                    <div id="message-bar-placeholder"></div>
                                </StackItem>
                                <StackItem>
                                    <Navigation userRoles={userInfo.roles} selectedNav={props.selectedNav}/>
                                </StackItem>
                                <StackItem className={styles.contentSection}>
                                    <RBAC userRoles={userInfo.roles} allowedRoles={props.allowedRoles}>
                                        {props.comp}
                                    </RBAC>
                                </StackItem>
                            </Stack>
                        </StackItem>
                        <StackItem>
                            <Footer/>
                        </StackItem>
                    </Stack>
                </>;
        } else {
            component = <><Login/></>
        }
    }
    return (
        <>{component}</>
    );
}

export function displayMessageBar(type: MessageBarType, text: string, spinner: boolean) {
    const messageDiv = document.getElementById("message-bar-placeholder");
    if (messageDiv) {
        ReactDOM.unmountComponentAtNode(messageDiv);

        // Only render the MessageBar if there's text to display
        if (text) {
            ReactDOM.render(
                <MessageBar
                    messageBarType={type}
                    isMultiline={false}
                    onDismiss={() => {
                        if (messageDiv) {
                            ReactDOM.unmountComponentAtNode(messageDiv);
                        }
                    }}
                >
                    <Stack horizontal tokens={{childrenGap: 10}}>
                        {spinner &&
                            <StackItem>
                                <Spinner size={SpinnerSize.xSmall}></Spinner>
                            </StackItem>
                        }
                        <StackItem>
                            {text}
                        </StackItem>
                    </Stack>
                </MessageBar>, messageDiv
            );
        }
    }
}

export default LayoutWithAuth;
